/**
 * Wiedervorlage — Fälligkeits-Zustand und Farbgebung.
 *
 * Spiegelt die Scopes des Backends (`GET /v2/admin/pb/wiedervorlage-faellig`,
 * `scope=today|overdue|due`), damit Liste und Drawer dieselbe Definition von
 * „fällig" benutzen wie die serverseitige Abfrage:
 *
 *   today    — Wiedervorlage-Datum ist heute   → gelb markiert
 *   overdue  — Datum liegt in der Vergangenheit → rot markiert
 *   upcoming — Datum liegt in der Zukunft       → keine Markierung
 *   none     — kein Datum gesetzt               → keine Markierung
 *
 * Verglichen wird kalendertagsgenau in lokaler Zeit (nicht per Date-Objekt-
 * Differenz): ein Datum „heute" darf abends nicht plötzlich als überfällig
 * gelten, und ein UTC-Zeitstempel darf den Tag nicht verschieben.
 */

import { toBerlinDateKey } from '../../lib/localDate';

export type WiedervorlageState = 'none' | 'upcoming' | 'today' | 'overdue';

/** yyyy-mm-dd in lokaler Zeit. */
function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * @param value  Wiedervorlage-Datum (ISO-String oder yyyy-mm-dd) oder null.
 * @param today  Referenztag — injizierbar, damit Tests nicht von „heute" abhängen.
 */
export function wiedervorlageState(
  value: string | null | undefined,
  today: Date = new Date(),
): WiedervorlageState {
  if (!value) return 'none';
  const day = toBerlinDateKey(String(value));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return 'none';
  const ref = localDay(today);
  if (day === ref) return 'today';
  return day < ref ? 'overdue' : 'upcoming';
}

export interface WiedervorlageTone {
  /** Hintergrund der markierten Zeile / des markierten Blocks. */
  bg: string;
  /** Rahmen-/Akzentfarbe. */
  border: string;
  /** Textfarbe für Datum und Badge. */
  text: string;
  /** Hintergrund des Status-Badges (kontrastiert gegen `bg`). */
  badgeBg: string;
  /** i18n-Key der Kurzbeschriftung. */
  labelKey: string;
}

/**
 * Farbtöne je Zustand und Theme.
 *
 * Getrennte Sätze für hell und dunkel: feste Hellwerte (#fffaeb / #fef3f2)
 * würden im Dark Mode zusammen mit `t.muted` (#8392a7) auf ~3,05:1 Kontrast
 * fallen — WCAG 2.1 AA verlangt 4,5:1 für Text dieser Größe. Im Dark Mode
 * liegen deshalb gedeckte, dunkle Flächen mit hellen Akzenten darauf.
 */
const TONES_LIGHT: Record<'today' | 'overdue', WiedervorlageTone> = {
  today: {
    bg: '#fffaeb',
    border: '#fedf89',
    text: '#b54708',
    badgeBg: '#ffffff',
    labelKey: 'anfragen.wiedervorlage.state.today',
  },
  overdue: {
    bg: '#fef3f2',
    border: '#fecdca',
    text: '#b42318',
    badgeBg: '#ffffff',
    labelKey: 'anfragen.wiedervorlage.state.overdue',
  },
};

const TONES_DARK: Record<'today' | 'overdue', WiedervorlageTone> = {
  today: {
    bg: '#2a2008',
    border: '#5c4708',
    text: '#fec84b',
    badgeBg: '#1a1405',
    labelKey: 'anfragen.wiedervorlage.state.today',
  },
  overdue: {
    bg: '#2d1211',
    border: '#5c1f1b',
    text: '#fda29b',
    badgeBg: '#1c0b0a',
    labelKey: 'anfragen.wiedervorlage.state.overdue',
  },
};

/**
 * Farbton für einen Zustand, oder undefined wenn nichts zu markieren ist
 * (`none` / `upcoming` → normaler Zeilenhintergrund bleibt stehen).
 */
export function wiedervorlageHighlight(
  state: WiedervorlageState,
  dark = false,
): WiedervorlageTone | undefined {
  if (state !== 'today' && state !== 'overdue') return undefined;
  return (dark ? TONES_DARK : TONES_LIGHT)[state];
}

/** Direktzugriff auf die Hell-Töne — für Tests und Farbvergleiche. */
export const WIEDERVORLAGE_TONE = TONES_LIGHT;
